import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { MultiSelect } from '../components/MultiSelect';
import './Reports.css';

interface ConversionData {
  id: string;
  date: string;
  time: string;
  clickTime: string;
  ctit?: string;
  clickId: string;
  offer: string;
  offerId: number;
  advertiser: string;
  advertiserId: number;
  affiliate: string;
  affiliateId: number;
  eventType: string;
  txnId?: string;
  revenue?: number;
  payout?: number;
  currency: string;
  status: 'pending' | 'approved' | 'rejected' | 'blocked';
  postbackFired?: boolean;
  attributionWindow: number;
}

interface Advertiser {
  id: number;
  name: string;
}

interface Affiliate {
  id: number;
  name: string;
}

const ConversionReport: React.FC = () => {
  // Set default date range to last 30 days
  const getDefaultDates = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDates());
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [advertiserFilter, setAdvertiserFilter] = useState<(number | string)[]>([]);
  const [affiliateFilter, setAffiliateFilter] = useState<(number | string)[]>([]);
  const [goalTypeFilter, setGoalTypeFilter] = useState<(number | string)[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [conversions, setConversions] = useState<ConversionData[]>([]);
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [goalTypes, setGoalTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [conversionDetails, setConversionDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch advertisers, affiliates, and goal types on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [advertisersRes, affiliatesRes, goalTypesRes] = await Promise.all([
          apiService.getAdvertisers(),
          apiService.getPublishers(),
          apiService.getGoalTypes()
        ]);
        setAdvertisers(advertisersRes.advertisers || []);
        setAffiliates(affiliatesRes.publishers || []);
        setGoalTypes(goalTypesRes.goalTypes || []);
      } catch (error) {
        console.error('Failed to fetch filters:', error);
      }
    };
    fetchFilters();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, statusFilter, advertiserFilter, affiliateFilter, goalTypeFilter]);

  // Fetch conversions when filters or page change
  useEffect(() => {
    const fetchConversions = async () => {
      try {
        setLoading(true);
        const response = await apiService.getConversions({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          advertiserIds: advertiserFilter.length > 0 ? advertiserFilter.join(',') : undefined,
          affiliateIds: affiliateFilter.length > 0 ? affiliateFilter.join(',') : undefined,
          goalTypes: goalTypeFilter.length > 0 ? goalTypeFilter.join(',') : undefined,
          limit: itemsPerPage,
          page: currentPage
        });
        setConversions(response.data || []);

        // Update pagination metadata
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalCount(response.pagination.total);
        }
      } catch (error: any) {
        console.error('Failed to fetch conversions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConversions();
  }, [dateRange, statusFilter, advertiserFilter, affiliateFilter, goalTypeFilter, currentPage, itemsPerPage]);

  const filteredConversions = conversions.filter(conv => {
    const searchMatch = searchTerm === '' ||
                       conv.offer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       conv.advertiser.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       conv.affiliate.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       conv.clickId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       conv.offerId.toString().includes(searchTerm) ||
                       conv.txnId?.toLowerCase().includes(searchTerm.toLowerCase());

    return searchMatch;
  });

  // Calculate stats based on conversion data from API
  // Note: conversions array contains data based on current filters (date, advertiser, affiliate, postback status)
  // But stats should show totals for the date range regardless of postback filter
  const [summaryStats, setSummaryStats] = useState({
    total: 0,
    totalPayout: 0,
    totalRevenue: 0
  });

  // Fetch summary stats separately (always for date range, ignoring status filter)
  useEffect(() => {
    const fetchSummaryStats = async () => {
      try {
        const response = await apiService.getConversions({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          advertiserIds: advertiserFilter.length > 0 ? advertiserFilter.join(',') : undefined,
          affiliateIds: affiliateFilter.length > 0 ? affiliateFilter.join(',') : undefined,
          limit: 10000 // Get all for stats calculation
        });
        const allConversions = response.data || [];

        setSummaryStats({
          total: allConversions.length,
          // Payout = only fired conversions
          totalPayout: allConversions.reduce((sum: number, c: any) => {
            // Check if conversion has postback_fired data, fallback to checking details
            const isFired = c.postbackFired !== undefined ? c.postbackFired : true; // assume fired if not specified
            return sum + (isFired ? (c.payout || 0) : 0);
          }, 0),
          // Revenue = all conversions
          totalRevenue: allConversions.reduce((sum: number, c: any) => sum + (c.revenue || 0), 0)
        });
      } catch (error) {
        console.error('Failed to fetch summary stats:', error);
      }
    };
    fetchSummaryStats();
  }, [dateRange, advertiserFilter, affiliateFilter]);

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'danger';
      case 'blocked': return 'dark';
      default: return 'secondary';
    }
  };

  const getEventTypeClass = (eventType: string) => {
    switch (eventType) {
      case 'install': return 'install';
      case 'lead': return 'lead';
      case 'sale': return 'sale';
      case 'event': return 'event';
      default: return 'default';
    }
  };

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportData = async (format: string) => {
    try {
      if (totalCount === 0) {
        alert('No data to export');
        return;
      }

      // Show loading message
      const confirmExport = window.confirm(
        `This will export all ${totalCount} conversions. This may take a few moments. Continue?`
      );
      if (!confirmExport) return;

      // Fetch ALL conversions (not just current page)
      const response = await apiService.getConversions({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        advertiserIds: advertiserFilter.length > 0 ? advertiserFilter.join(',') : undefined,
        affiliateIds: affiliateFilter.length > 0 ? affiliateFilter.join(',') : undefined,
        goalTypes: goalTypeFilter.length > 0 ? goalTypeFilter.join(',') : undefined,
        limit: 100000 // Get all conversions
      });

      const allConversions = response.data || [];

      // Apply search filter
      const conversionsToExport = allConversions.filter((conv: any) => {
        if (searchTerm === '') return true;
        return conv.offer.toLowerCase().includes(searchTerm.toLowerCase()) ||
               conv.advertiser.toLowerCase().includes(searchTerm.toLowerCase()) ||
               conv.affiliate.toLowerCase().includes(searchTerm.toLowerCase()) ||
               conv.clickId.toLowerCase().includes(searchTerm.toLowerCase()) ||
               conv.offerId.toString().includes(searchTerm) ||
               conv.txnId?.toLowerCase().includes(searchTerm.toLowerCase());
      });

      // Fetch detailed data for all conversions to export
      const detailedConversions = await Promise.all(
        conversionsToExport.map(async (conv: any) => {
          try {
            const response = await apiService.getConversionDetails(conv.id);
            return {
              ...conv,
              details: response.data || conv.details || null
            };
          } catch (error) {
            console.error(`Failed to fetch details for conversion ${conv.id}:`, error);
            return { ...conv, details: conv.details || null };
          }
        })
      );

      if (format === 'CSV') {
        exportToCSV(detailedConversions);
      } else if (format === 'Excel') {
        exportToExcel(detailedConversions);
      } else if (format === 'PDF') {
        exportToPDF(detailedConversions);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data');
    }
  };

  const exportToCSV = (data: any[]) => {
    const headers = [
      'Date',
      'Time',
      'Click Time',
      'CTIT',
      'Offer ID',
      'Offer',
      'Affiliate ID',
      'Affiliate',
      'Advertiser',
      'Revenue',
      'Payout',
      'Click ID',
      'Status',
      'Event Type',
      'Affiliate Click ID',
      'Click IP',
      'Install/Event IP',
      'Country',
      'State',
      'City',
      'OS',
      'GAID',
      'IFAID',
      'Device ID',
      'Sub2',
      'Sub3',
      'Sub4',
      'Sub5',
      'Block Reason',
      'Sub Block Reason',
      'Final URL',
      'Postback URL',
      'Postback Fired'
    ];

    const rows = data.map(conv => [
      conv.date,
      conv.time,
      conv.clickTime,
      conv.ctit || '-',
      conv.offerId,
      conv.offer,
      conv.affiliateId || '-',
      conv.affiliate,
      conv.advertiser,
      `$${(conv.revenue || 0).toFixed(2)}`,
      `$${(conv.payout || 0).toFixed(2)}`,
      conv.clickId,
      conv.status,
      conv.eventType,
      conv.details?.clickDetails?.aff_sub1 || 'N/A',
      conv.details?.clickDetails?.click_ip || 'N/A',
      conv.details?.conversion_ip || 'N/A',
      conv.details?.clickDetails?.country || 'N/A',
      conv.details?.clickDetails?.state || 'N/A',
      conv.details?.clickDetails?.city || 'N/A',
      conv.details?.clickDetails?.os || 'N/A',
      conv.details?.clickDetails?.gaid || 'N/A',
      conv.details?.clickDetails?.ifaid || 'N/A',
      conv.details?.clickDetails?.device_id || 'N/A',
      conv.details?.clickDetails?.aff_sub2 || 'N/A',
      conv.details?.clickDetails?.aff_sub3 || 'N/A',
      conv.details?.clickDetails?.aff_sub4 || 'N/A',
      conv.details?.clickDetails?.aff_sub5 || 'N/A',
      conv.details?.block_reason || 'N/A',
      conv.details?.sub_block_reason || 'N/A',
      conv.details?.clickDetails?.final_url || 'N/A',
      conv.details?.postback_url || 'N/A',
      conv.details?.postback_fired ? 'Yes' : 'No'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `conversion_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportToExcel = (data: any[]) => {
    // For Excel, we'll export as CSV with .xlsx extension
    // In a production app, you'd use a library like xlsx or exceljs
    const headers = [
      'Date',
      'Time',
      'Click Time',
      'CTIT',
      'Offer ID',
      'Offer',
      'Affiliate ID',
      'Affiliate',
      'Advertiser',
      'Revenue',
      'Payout',
      'Click ID',
      'Status',
      'Event Type',
      'Affiliate Click ID',
      'Click IP',
      'Install/Event IP',
      'Country',
      'State',
      'City',
      'OS',
      'GAID',
      'IFAID',
      'Device ID',
      'Sub2',
      'Sub3',
      'Sub4',
      'Sub5',
      'Block Reason',
      'Sub Block Reason',
      'Final URL',
      'Postback URL',
      'Postback Fired'
    ].join('\t');

    const rows = data.map(conv => [
      conv.date,
      conv.time,
      conv.clickTime,
      conv.ctit || '-',
      conv.offerId,
      conv.offer,
      conv.affiliateId || '-',
      conv.affiliate,
      conv.advertiser,
      (conv.revenue || 0).toFixed(2),
      (conv.payout || 0).toFixed(2),
      conv.clickId,
      conv.status,
      conv.eventType,
      conv.details?.clickDetails?.aff_sub1 || 'N/A',
      conv.details?.clickDetails?.click_ip || 'N/A',
      conv.details?.conversion_ip || 'N/A',
      conv.details?.clickDetails?.country || 'N/A',
      conv.details?.clickDetails?.state || 'N/A',
      conv.details?.clickDetails?.city || 'N/A',
      conv.details?.clickDetails?.os || 'N/A',
      conv.details?.clickDetails?.gaid || 'N/A',
      conv.details?.clickDetails?.ifaid || 'N/A',
      conv.details?.clickDetails?.device_id || 'N/A',
      conv.details?.clickDetails?.aff_sub2 || 'N/A',
      conv.details?.clickDetails?.aff_sub3 || 'N/A',
      conv.details?.clickDetails?.aff_sub4 || 'N/A',
      conv.details?.clickDetails?.aff_sub5 || 'N/A',
      conv.details?.block_reason || 'N/A',
      conv.details?.sub_block_reason || 'N/A',
      conv.details?.clickDetails?.final_url || 'N/A',
      conv.details?.postback_url || 'N/A',
      conv.details?.postback_fired ? 'Yes' : 'No'
    ].join('\t')).join('\n');

    const excelContent = headers + '\n' + rows;
    const blob = new Blob([excelContent], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `conversion_report_${new Date().toISOString().split('T')[0]}.xls`;
    link.click();
  };

  const exportToPDF = (data: any[]) => {
    // Create a simple HTML table for PDF
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Conversion Report</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 10px; }
          h1 { text-align: center; color: #333; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #4CAF50; color: white; }
          tr:nth-child(even) { background-color: #f2f2f2; }
        </style>
      </head>
      <body>
        <h1>Conversion Report</h1>
        <p>Generated: ${new Date().toLocaleString()}</p>
        <table>
          <thead>
            <tr>
              <th>Date/Time</th>
              <th>Click Time</th>
              <th>CTIT</th>
              <th>Offer</th>
              <th>Advertiser</th>
              <th>Affiliate</th>
              <th>Revenue</th>
              <th>Payout</th>
              <th>Status</th>
              <th>Country</th>
              <th>City</th>
              <th>OS</th>
            </tr>
          </thead>
          <tbody>
            ${data.map(conv => `
              <tr>
                <td>${conv.date} ${conv.time}</td>
                <td>${conv.clickTime}</td>
                <td>${conv.ctit || '-'}</td>
                <td>${conv.offer}</td>
                <td>${conv.advertiser}</td>
                <td>${conv.affiliate}</td>
                <td>$${(conv.revenue || 0).toFixed(2)}</td>
                <td>$${(conv.payout || 0).toFixed(2)}</td>
                <td>${conv.status}</td>
                <td>${conv.details?.clickDetails?.country || 'N/A'}</td>
                <td>${conv.details?.clickDetails?.city || 'N/A'}</td>
                <td>${conv.details?.clickDetails?.os || 'N/A'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `conversion_report_${new Date().toISOString().split('T')[0]}.html`;
    link.click();

    alert('PDF export: HTML file downloaded. Please print to PDF from your browser.');
  };

  const handleRowClick = async (conversionId: string) => {
    if (expandedRow === conversionId) {
      // Collapse if already expanded
      setExpandedRow(null);
      setConversionDetails(null);
    } else {
      // Expand and fetch details
      setExpandedRow(conversionId);
      setLoadingDetails(true);
      try {
        const response = await apiService.getConversionDetails(conversionId);
        setConversionDetails(response.data);
      } catch (error) {
        console.error('Failed to fetch conversion details:', error);
        alert('Failed to load conversion details');
      } finally {
        setLoadingDetails(false);
      }
    }
  };

  const handleFirePostback = async (conversionId: string) => {
    if (!window.confirm('Are you sure you want to fire the postback for this conversion?')) {
      return;
    }

    try {
      await apiService.firePostback(conversionId);
      alert('Postback fired successfully!');

      // Refresh the details
      const response = await apiService.getConversionDetails(conversionId);
      setConversionDetails(response.data);
    } catch (error: any) {
      console.error('Failed to fire postback:', error);
      alert(error.response?.data?.error || 'Failed to fire postback');
    }
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Conversion Report</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="report-summary" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="summary-card">
          <h3>Total Conversions</h3>
          <div className="value">{summaryStats.total.toLocaleString()}</div>
          <small style={{ color: '#888', fontSize: '12px' }}>All conversions in date range</small>
        </div>
        <div className="summary-card">
          <h3>Total Payout</h3>
          <div className="value" style={{ color: '#e74c3c' }}>${summaryStats.totalPayout.toFixed(2)}</div>
          <small style={{ color: '#888', fontSize: '12px' }}>Only fired postbacks</small>
        </div>
        <div className="summary-card">
          <h3>Total Revenue</h3>
          <div className="value" style={{ color: '#27ae60' }}>${summaryStats.totalRevenue.toFixed(2)}</div>
          <small style={{ color: '#888', fontSize: '12px' }}>All conversions</small>
        </div>
      </div>

      {/* Filters */}
      <div className="report-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>Start Date:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              className="date-input"
            />
          </div>
          <div className="filter-group">
            <label>End Date:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              className="date-input"
            />
          </div>
          <div className="filter-group">
            <MultiSelect
              options={advertisers}
              value={advertiserFilter}
              onChange={setAdvertiserFilter}
              placeholder="All Advertisers"
              label="Advertiser:"
            />
          </div>
          <div className="filter-group">
            <MultiSelect
              options={affiliates}
              value={affiliateFilter}
              onChange={setAffiliateFilter}
              placeholder="All Affiliates"
              label="Affiliate:"
            />
          </div>
          <div className="filter-group">
            <label>Postback Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-select"
            >
              <option value="all">All</option>
              <option value="fired">Fired</option>
              <option value="not_fired">Not Fired</option>
            </select>
          </div>
          <div className="filter-group">
            <MultiSelect
              options={goalTypes.map(type => ({ id: type, name: type }))}
              value={goalTypeFilter}
              onChange={setGoalTypeFilter}
              placeholder="All Goal Types"
              label="Goal Type:"
            />
          </div>
        </div>

        <div className="filters-row" style={{ marginTop: '10px' }}>
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search conversions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="export-buttons" style={{ marginLeft: 'auto' }}>
            <button className="btn btn-success" onClick={() => exportData('CSV')}>
              CSV
            </button>
            <button className="btn btn-warning" onClick={() => exportData('Excel')}>
              Excel
            </button>
            <button className="btn btn-danger" onClick={() => exportData('PDF')}>
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* Report Table */}
      <div className="report-content">
        <div className="table-container">
          <table className="report-table conversion-table">
            <thead>
              <tr>
                <th>Date/Time</th>
                <th>Click Time</th>
                <th>CTIT</th>
                <th>Offer ID</th>
                <th>Offer</th>
                <th>Affiliate ID</th>
                <th>Advertiser</th>
                <th>Revenue</th>
                <th>Payout</th>
                <th>Click ID</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>
                    Loading conversions...
                  </td>
                </tr>
              ) : filteredConversions.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>
                    No conversions found for the selected filters.
                  </td>
                </tr>
              ) : (
                filteredConversions.map((conv) => (
                  <React.Fragment key={conv.id}>
                    <tr
                      onClick={() => handleRowClick(conv.id)}
                      className={`conversion-row ${expandedRow === conv.id ? 'is-expanded' : ''}`}
                    >
                      <td>
                        <div className="datetime-cell">
                          <span className="date">{conv.date}</span>
                          <span className="time">{conv.time}</span>
                        </div>
                      </td>
                      <td>
                        <span className="time">{conv.clickTime}</span>
                      </td>
                      <td>
                        <span className="ctit">{conv.ctit || '-'}</span>
                      </td>
                      <td>
                        <span className="offer-id">{conv.offerId}</span>
                      </td>
                      <td>
                        <span className="offer-name">{conv.offer}</span>
                      </td>
                      <td>
                        <span className="affiliate-id">{conv.affiliateId || '-'}</span>
                      </td>
                      <td>
                        <span className="advertiser-name">{conv.advertiser}</span>
                      </td>
                      <td>
                        <span className="revenue">
                          ${(conv.revenue || 0).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <span className="payout">${(conv.payout || 0).toFixed(2)}</span>
                      </td>
                      <td>
                        <span className="click-id">{conv.clickId}</span>
                      </td>
                    </tr>
                    {expandedRow === conv.id && (
                      <tr className="conversion-expanded-row">
                        <td colSpan={10} className="conversion-expanded-cell">
                          {loadingDetails ? (
                            <div className="conversion-details-loading">Loading details...</div>
                          ) : conversionDetails ? (
                            <div className="conversion-details">
                              <div className="conversion-details-grid">
                                <div>
                                  <h4 className="conversion-details-title">Click Details</h4>
                                  <table className="conversion-details-table">
                                    <tbody>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">Affiliate Click ID:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.aff_sub1 || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">Click IP:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.click_ip || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">IP (Install/Event):</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.conversion_ip}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">Country:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.country || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">State:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.state || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">City:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.city || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">OS:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.os || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">GAID:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.gaid || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">IFAID:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.ifaid || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">Device ID:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.device_id || 'N/A'}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                                <div>
                                  <h4 className="conversion-details-title">Additional Details</h4>
                                  <table className="conversion-details-table">
                                    <tbody>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">Sub2:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.aff_sub2 || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">Sub3:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.aff_sub3 || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">Sub4:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.aff_sub4 || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">Sub5:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.clickDetails?.aff_sub5 || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">Block Reason:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.block_reason || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">Sub Block Reason:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.sub_block_reason || 'N/A'}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">Event Name:</td>
                                        <td className="conversion-details-value-cell">{conversionDetails.event_type}</td>
                                      </tr>
                                      <tr className="conversion-details-row">
                                        <td className="conversion-details-label-cell">Status:</td>
                                        <td className="conversion-details-value-cell">
                                          <span className={`status-badge ${getStatusClass(conversionDetails.status)}`}>
                                            {conversionDetails.status.charAt(0).toUpperCase() + conversionDetails.status.slice(1)}
                                          </span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              <div className="conversion-details-section">
                                <h4 className="conversion-details-title">Final URL</h4>
                                <div className="conversion-details-url">
                                  {conversionDetails.clickDetails?.final_url || 'N/A'}
                                </div>
                              </div>

                              <div className="conversion-details-section">
                                <h4 className="conversion-details-title">Postback URL</h4>
                                <div className="conversion-details-url">
                                  {conversionDetails.postback_url}
                                </div>
                              </div>

                              <div className="conversion-details-postback">
                                <strong className="conversion-details-label-inline">Fire Postback:</strong>
                                {conversionDetails.postback_fired ? (
                                  <span className="conversion-postback-fired">
                                    ✓ Fired
                                    {conversionDetails.postback_fire_type ? (
                                      <span className="conversion-postback-type">
                                        (
                                        {conversionDetails.postback_fire_type === 'auto_likely'
                                          ? 'Auto'
                                          : conversionDetails.postback_fire_type === 'manual_likely'
                                            ? 'Manual'
                                            : conversionDetails.postback_fire_type}
                                        )
                                      </span>
                                    ) : null}
                                  </span>
                                ) : (
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFirePostback(conv.id);
                                    }}
                                  >
                                    Fire Postback
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="conversion-details-error">Failed to load details</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="table-footer">
        <div className="table-info">
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} conversions
        </div>
        <div className="pagination">
          <button
            className="page-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          {/* Generate page number buttons */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            className="page-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversionReport;
